import { inject } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import type { Store } from "../../../../client/stores";
import { mediaServerCatalogImage } from "../../../../client/utils";
import { Sizes, SrcSet } from "../../components/ResponsiveImage.component";
import * as Webapi from "../../generated/webapi.g";
import { placeholderImage } from "../../lib/image";
import { ImageTile } from "../../ui";

export interface PodcastTileProps {
    host: string;
    podcast: Webapi.PodcastFragment;
    context?: string;
    store?: Store;
}

export const PodcastTile = inject("store")((props: PodcastTileProps) => {
    if (!props.store) {
        return null;
    }
    const { t } = useTranslation();
    const img = mediaServerCatalogImage(props.host, "podcast", props.podcast.id, 48, 48, true);
    const srcset: SrcSet[] = PodcastSrcSet(img);
    const sizes: Sizes[] = PodcastSizes();
    const placeholderSrcset: SrcSet[] = PodcastSrcSet(placeholderImage);
    const podcastProps = {
        title: props.podcast.title,
        url: `${props.store.env.NATIONAL_SITE_URL}/podcast/${props.podcast.slug}-${props.podcast.id}/`,
        imageUrl: `${img}?ops=max(270,270),quality(70)`,
        responsiveImg: {
            src: `${img}?ops=max(270,270),quality(70)`,
            alt: t("podcast_alt", { title: props.podcast.title }),
            srcset,
            sizes,
            placeholderSrcset,
            initialWidth: 150,
            initialHeight: 150,
        },
        context: props.context,
    };
    return <ImageTile {...podcastProps} />;
});

export const PodcastSrcSet = (imgUri: string) => [
    {
        url: `${imgUri}?ops=max(150,150),quality(70)`,
        descriptor: "360w",
    },
    {
        url: `${imgUri}?ops=max(270,270),quality(70)`,
        descriptor: "600w",
    },
    {
        url: `${imgUri}?ops=max(380,380),quality(70)`,
        descriptor: "800w",
    },
    {
        url: `${imgUri}?ops=max(480,480),quality(70)`,
        descriptor: "1060w",
    },
    {
        url: `${imgUri}?ops=max(250,250),quality(70)`,
        descriptor: "1250w",
    },
    {
        url: `${imgUri}?ops=max(300,300),quality(70)`,
        descriptor: "1251w",
    },
];

export const PodcastSizes = () => [
    {
        media: "(max-width: 359px)",
        size: "360px",
    },
    {
        media: "(max-width: 599px)",
        size: "600px",
    },
    {
        media: "(max-width: 759px)",
        size: "800px",
    },
    {
        media: "(max-width: 1059px)",
        size: "1060px",
    },
    {
        media: "(max-width: 1249px)",
        size: "1250px",
    },
    {
        media: "any",
        size: "1251px",
    },
];

export default PodcastTile;
